import { Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { OverviewCardRow } from "@/lib/dashboard/OverviewCardRow";
import { useI18n } from "@/hooks/useI18n";

interface ClientsAndTripsCardProps {
  clientsCount: number;
  activeCount: number;
  completedCount: number;
  /** Conteggi di piattaforma: valorizzati solo per admin, `null` per tutti gli altri. */
  agenciesCount: number | null;
  advisorsCount: number | null;
}

/**
 * Quante persone e quante pratiche: la card dei conteggi.
 *
 * Il numero grande sono i clienti, perche' e' la domanda che si fa per prima aprendo una
 * dashboard di agenzia; pratiche in corso e completate stanno sotto come righe, perche'
 * sono la scomposizione del lavoro, non due totali indipendenti.
 *
 * Le righe di piattaforma (agenzie, travel advisor) compaiono solo quando la RPC le
 * valorizza -- per l'admin. Il controllo e' sul `null` e non sul ruolo: cosi' la regola di
 * visibilita' vive in un posto solo, il database.
 */
export function ClientsAndTripsCard({
  clientsCount,
  activeCount,
  completedCount,
  agenciesCount,
  advisorsCount,
}: ClientsAndTripsCardProps) {
  const { t } = useI18n();
  const hasPlatformRows = agenciesCount != null || advisorsCount != null;

  return (
    <Card className="h-full">
      <CardContent className="p-4 h-full flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg flex-shrink-0 bg-indigo-50 text-indigo-600">
            <Users className="h-4 w-4" />
          </div>
          <span className="text-sm text-gray-500">
            {t("dashboard.overview.clientsAndTrips.title")}
            {" "}
            <InfoTooltip text={t("dashboard.overview.clientsAndTrips.info")} />
          </span>
        </div>

        <div>
          <div className="text-2xl font-semibold text-gray-900 tabular-nums">
            {clientsCount}
          </div>
          <span className="text-xs text-gray-500">
            {t("dashboard.overview.clientsAndTrips.clients")}
            {" "}
            <InfoTooltip text={t("dashboard.overview.clientsAndTrips.clientsInfo")} />
          </span>
        </div>

        <div className="flex flex-col gap-1.5 mt-auto pt-1 border-t border-gray-100">
          <OverviewCardRow
            label={t("dashboard.overview.clientsAndTrips.tripsActive")}
            value={String(activeCount)}
            dotClassName="bg-amber-400"
            info={t("dashboard.overview.clientsAndTrips.tripsActiveInfo")}
          />
          <OverviewCardRow
            label={t("dashboard.overview.clientsAndTrips.tripsCompleted")}
            value={String(completedCount)}
            dotClassName="bg-emerald-500"
            info={t("dashboard.overview.clientsAndTrips.tripsCompletedInfo")}
          />

          {hasPlatformRows && (
            <div className="flex flex-col gap-1.5 pt-1.5 border-t border-gray-100">
              {agenciesCount != null && (
                <OverviewCardRow
                  label={t("dashboard.overview.clientsAndTrips.agencies")}
                  value={String(agenciesCount)}
                  info={t("dashboard.overview.clientsAndTrips.agenciesInfo")}
                />
              )}
              {advisorsCount != null && (
                <OverviewCardRow
                  label={t("dashboard.overview.clientsAndTrips.advisors")}
                  value={String(advisorsCount)}
                  info={t("dashboard.overview.clientsAndTrips.advisorsInfo")}
                />
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ClientsAndTripsCard;
